import { ethers } from "ethers";
import {
  SHIELDED_POOL_JOINSPLIT_V2_INTERFACE,
  buildFlatJoinSplitV2Transaction,
  buildFlatMigrateV2Transaction,
  buildFlatPrivateBridgeV2Transaction,
  buildFlatPrivateSwapV2Transaction,
  buildFlatWithdrawBatchV2Transaction,
  buildFlatWithdrawV2Transaction,
  validateFlatJoinSplitV2WithdrawBatchEntries,
  computeFlatJoinSplitV2BridgeAuxHash,
  computeFlatJoinSplitV2SwapAuxHash,
  proveFlatJoinSplitV2,
  readFlatJoinSplitV2PoolSnapshot,
  resolveFlatJoinSplitV2Action,
  selectFlatJoinSplitV2Arity,
  simulateFlatJoinSplitV2Transaction,
  type FlatJoinSplitV2BridgeData,
  type FlatJoinSplitV2Hex32,
  type FlatJoinSplitV2HexData,
  type FlatJoinSplitV2InputNoteWitness,
  type FlatJoinSplitV2OutputNoteWitness,
  type FlatJoinSplitV2ProveRequest,
  type FlatJoinSplitV2SwapData,
} from "./sdk";
import { EVM_CONFIG } from "../config";
import {
  submitBasePoolRelay,
  serializeTransaction,
} from "../utils/relayer-client";
import { computeNPK, computeNullifier } from "../joinsplit/keys";
import type {
  JoinSplitNote,
  SpendingKeys,
  StoredJoinSplitNote,
} from "../joinsplit/types";
import {
  scanFlatJoinSplitV2Tree,
  type FlatJoinSplitV2MerklePath,
} from "./merkleIndex";
import {
  assertFlatJoinSplitV2Note,
  createFlatJoinSplitV2Note,
  flatFieldHex,
} from "./notes";
import { withFlatJoinSplitV2Prover } from "./prover";
import { assertAscendingLeafIndices, selectForAmount } from "./spendSelection";
import {
  markFlatJoinSplitV2SettlementBroadcast,
  markFlatJoinSplitV2SettlementFinalized,
  markFlatJoinSplitV2SettlementIncluded,
  prepareFlatJoinSplitV2SettlementJournal,
  type FlatJoinSplitV2JournalDelivery,
  type FlatJoinSplitV2JournalKind,
  type FlatJoinSplitV2JournalScope,
} from "./pendingJournal";
import { fail } from "./common";
import type {
  FlatJoinSplitV2OperationResult,
  FlatJoinSplitV2TransferParams,
} from "./operationTypes";
import {
  authenticateAndDedupeCandidates,
  candidateNotes,
} from "./noteSelection";
import {
  readCurrentTokenDomain,
  requireEnabled,
  requireProvider,
} from "./poolAccess";
import { proveAndSend } from "./proveAndSend";
import { selfDeliveries } from "./witness";
import { usesPrivateFees, executePrivateFeeSpend } from "./privateFees";

export async function executeFlatJoinSplitV2Transfer(
  params: FlatJoinSplitV2TransferParams,
): Promise<FlatJoinSplitV2OperationResult> {
  if (usesPrivateFees()) return executePrivateFeeSpend(params, 0);
  try {
    const pool = requireEnabled();
    const tokenAddress = ethers.getAddress(params.tokenAddress);
    const { provider, chainId } = await requireProvider(params.signer);
    const targetDomain = await readCurrentTokenDomain(
      provider,
      pool,
      tokenAddress,
    );
    const currentTreeIndex = targetDomain.treeIndex;
    const available = (
      await authenticateAndDedupeCandidates({
        candidates: candidateNotes(params.notes, pool, chainId, tokenAddress),
        pool,
        chainId,
        tokenAddress,
        spendingKeys: params.spendingKeys,
      })
    ).filter(({ note }) => note.treeIndex === currentTreeIndex);
    const selected = selectForAmount(available, params.amount);
    const total = selected.reduce((sum, note) => sum + note.note.value, 0n);
    const tokenHash = flatFieldHex(selected[0].note.tokenHash);
    const recipientNote = {
      ...(await createFlatJoinSplitV2Note({
        recipientMpk: params.recipientMpk,
        chainId,
        poolAddress: pool,
        treeIndex: currentTreeIndex,
        tokenAddress,
        tokenHash,
        value: params.amount,
      })),
      privateHistory: params.recipientPrivateHistory,
    };
    const outputs: JoinSplitNote[] = [recipientNote];
    if (total > params.amount) {
      outputs.push(
        await createFlatJoinSplitV2Note({
          recipientMpk: params.spendingKeys.mpk,
          chainId,
          poolAddress: pool,
          treeIndex: currentTreeIndex,
          tokenAddress,
          tokenHash,
          value: total - params.amount,
        }),
      );
    }
    params.onLog("Preparing a depth-24 private transfer witness", "info");
    const settled = await proveAndSend({
      ...params,
      provider,
      pool,
      chainId,
      tokenAddress,
      selected,
      outputs,
      kind: "joinsplit",
      deliveries: outputs.map((note, index) => ({
        commitment: note.commitment,
        recipientMpkHex:
          index === 0
            ? flatFieldHex(params.recipientMpk)
            : flatFieldHex(params.spendingKeys.mpk),
        recipientInboxPublicKeyHex:
          index === 0 ? params.recipientInboxPublicKeyHex : undefined,
        keepLocal: index > 0 || params.recipientMpk === params.spendingKeys.mpk,
      })),
    });
    return {
      success: true,
      txHash: settled.txHash,
      spentNotes: selected.map((note) => note.note),
      outputNotes: settled.outputs,
      changeNote: settled.outputs[1],
      journalId: settled.journalId,
    };
  } catch (error) {
    return {
      success: false,
      spentNotes: [],
      outputNotes: [],
      error: error instanceof Error ? error.message : String(error),
    };
  }
}
